import { useEffect, useRef } from 'react';

export type WatchOnChange<T> = (newValue: T, previousValue: T, isInit: boolean) => void;

export type WatchArgs<T> = {
  value: T;
  onChange: WatchOnChange<T>;
  runOnInit?: boolean;
  /** Custom comparator function. Should return true if values are equal and false otherwise. */
  compare?: (next: T, previous: T) => boolean;
};

/**
 * Invoke the `onChange` callback when `value` changed.
 * To also invoke `onChange` on component mount set `runOnInit` to true.
 *
 * The `onChange` value can and should be a return value from `useCallback`;
 * if the `useCallback` dependencies and thus the callback value change,
 * it will not trigger `onChange`.
 */
export default function useWatch<T>(args: WatchArgs<T>) {
  const { value, onChange, runOnInit = false, compare } = args;
  const previousValueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Run only once onload
  useEffect(() => {
    if (runOnInit) {
      onChangeRef.current(value, previousValueRef.current, true);
    }
  }, []);

  useEffect(() => {
    const isEqual = compare?.(value, previousValueRef.current) ?? value === previousValueRef.current;
    if (!isEqual) {
      onChangeRef.current(value, previousValueRef.current, false);
      previousValueRef.current = value;
    }
  }, [value, compare]);
}
